import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Table, Input, Label, ButtonGroup, Col, Badge } from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";

// (2026-08) Desglose de cotizaciones folio por folio de UN vendedor.
// Sin vendedor seleccionado no se despliega información.
function ReporteCotizacionesVendedor() {
  const { user } = useContext(AuthContext);
  const URL_COTIZACIONES = process.env.REACT_APP_URL_COTIZACIONES;
  const URL_COLABORADORES = process.env.REACT_APP_URL_COLABORADORES;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 90);

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(
    moment(inicio).format("YYYY-MM-DD")
  );
  const [selectedFechaFin, setSelectedFechaFin] = useState(
    moment(hoy).format("YYYY-MM-DD")
  );

  const [cotizaciones, setCotizaciones] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [selectedVendedor, setSelectedVendedor] = useState("");
  const [cargando, setCargando] = useState(false);

  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };

  const arr = (d) => (Array.isArray(d) ? d : []);
  const money = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  const pct = (parte, total) =>
    Number(total || 0) > 0 ? (Number(parte || 0) / Number(total)) * 100 : 0;
  const fechaCorta = (f) => (f ? moment(f).format("DD/MM/YYYY") : "-");

  useEffect(() => {
    axios
      .get(URL_COLABORADORES, { headers })
      .then((r) => setColaboradores(arr(r.data)))
      .catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    setCargando(true);
    axios
      .get(`${URL_COTIZACIONES}Fechas/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers,
      })
      .then((r) => {
        const data = arr(r.data)
          .filter((c) => c.is_active !== "No")
          .map((c) => {
            const pedido = arr(c.pedidos)[0] || null;
            const colab = arr(c.colaboradores)[0] || {};
            return {
              _id: c._id,
              folio: c.idCotizacion,
              fecha: c.fecha,
              empresa: arr(c.empresas)[0]?.name || "SIN EMPRESA",
              vendedor: `${colab.nombre || ""} ${colab.apellido || ""}`.trim() || "SIN VENDEDOR",
              idVendedor: colab._id || "",
              cliente:
                arr(c.clientes)[0]?.nombre_comercial ||
                // (2026-10) cotización a un prospecto del CRM
                (arr(c.crmProspectos)[0]
                  ? `${arr(c.crmProspectos)[0].empresa || arr(c.crmProspectos)[0].razon_social} (Prospecto)`
                  : "SIN CLIENTE"),
              subtotal: Number(c.subTotal || c.total_general || 0),
              convertida: !!pedido,
              montoPedido: pedido ? Number(pedido.total_general || 0) : 0,
              fechaPedido: pedido ? pedido.fecha : "",
            };
          })
          .sort((a, b) => Number(b.folio) - Number(a.folio));
        setCotizaciones(data);
        setCargando(false);
      })
      .catch((e) => {
        console.log(e);
        setCargando(false);
        Swal.fire("Error", "No se pudieron cargar las cotizaciones", "error");
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  // Sin vendedor no hay información
  const filtradas = useMemo(
    () =>
      selectedVendedor
        ? cotizaciones.filter((c) => c.idVendedor === selectedVendedor)
        : [],
    [cotizaciones, selectedVendedor]
  );

  const vendedorNombre = useMemo(() => {
    const c = colaboradores.find((x) => x._id === selectedVendedor);
    return c ? `${c.nombre || ""} ${c.apellido || ""}`.trim() : "";
  }, [colaboradores, selectedVendedor]);

  const totalCotizado = filtradas.reduce((a, c) => a + c.subtotal, 0);
  const totalConvertido = filtradas.reduce((a, c) => a + c.montoPedido, 0);
  const convertidas = filtradas.filter((c) => c.convertida);
  const pendiente = totalCotizado - totalConvertido;

  // Empresas del vendedor, con el número de folios de cada una
  const empresasTexto = useMemo(() => {
    const m = {};
    filtradas.forEach((c) => {
      m[c.empresa] = (m[c.empresa] || 0) + 1;
    });
    return Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .map(([e, n]) => `${e} (${n} ${n === 1 ? "Folio" : "Folios"})`)
      .join(" / ");
  }, [filtradas]);

  // ---------------- PDF ----------------
  const PDFReporte = () => {
    if (!selectedVendedor) {
      return Swal.fire("Falta el vendedor", "Selecciona un vendedor", "warning");
    }
    if (!filtradas.length) {
      return Swal.fire(
        "Sin datos",
        "Ese vendedor no tiene cotizaciones en el periodo",
        "warning"
      );
    }

    const doc = new jsPDF("landscape", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const ML = 10;
    const usableW = pageW - ML * 2;

    const azulOscuro = [31, 56, 100];
    const azul = [46, 117, 182];
    const azulClaro = [232, 240, 250];
    const verde = [39, 124, 70];
    const verdeClaro = [235, 245, 237];
    const naranja = [214, 110, 40];
    const naranjaClaro = [253, 245, 235];
    const gris = [110, 120, 135];
    const bordeGris = [210, 216, 224];

    // ---- Banda de título
    doc.setFillColor(...azulOscuro);
    doc.rect(0, 0, pageW, 18, "F");
    doc.setTextColor(255).setFont(undefined, "bold").setFontSize(15);
    doc.text("LANGIANI ERP - DESGLOSE COMPLETO DE COTIZACIONES POR FOLIO", ML, 9);
    doc.setFont(undefined, "normal").setFontSize(9);
    doc.text(
      `Listado Individual Detallado - Vendedor: ${vendedorNombre} (${filtradas.length} Folios Cotizados)`,
      ML,
      14.5
    );
    doc.setTextColor(0);

    let y = 25;
    doc.setFontSize(8.5);
    const linea = (etiqueta, valor, yy, x, alineado) => {
      doc.setFont(undefined, "bold").setTextColor(...azulOscuro);
      if (alineado === "right") {
        doc.setFont(undefined, "normal").setTextColor(60);
        const wv = doc.getTextWidth(valor);
        doc.setFont(undefined, "bold").setTextColor(...azulOscuro);
        doc.text(etiqueta, x - wv - 1, yy, { align: "right" });
        doc.setFont(undefined, "normal").setTextColor(60);
        doc.text(valor, x, yy, { align: "right" });
      } else {
        const w = doc.getTextWidth(`${etiqueta} `);
        doc.text(etiqueta, x, yy);
        doc.setFont(undefined, "normal").setTextColor(60);
        doc.text(valor, x + w, yy);
      }
      doc.setTextColor(0);
    };

    linea("Vendedor:", vendedorNombre, y, ML);
    linea("Empresa Principal:", empresasTexto || "-", y + 4.5, ML);
    linea(
      "Rango de Fechas:",
      `${fechaCorta(selectedFechaInicio)} - ${fechaCorta(selectedFechaFin)}`,
      y + 9,
      ML
    );
    linea("Fecha de Emisión:", moment(hoy).format("DD/MM/YYYY"), y, pageW - ML, "right");
    linea("Módulo:", "Ventas & Cotizaciones ERP", y + 4.5, pageW - ML, "right");
    linea("Modalidad:", "Detalle Folio por Folio (Sin Agrupar)", y + 9, pageW - ML, "right");

    y += 14;

    // ---- Tarjetas
    const tarjetas = [
      {
        titulo: "TOTAL COTIZACIONES",
        valor: `${filtradas.length} Folios`,
        pie: "100% Desglosados",
        color: azul,
        fondo: azulClaro,
      },
      {
        titulo: "MONTO COTIZADO",
        valor: money(totalCotizado),
        pie: "Subtotal General",
        color: naranja,
        fondo: naranjaClaro,
      },
      {
        titulo: "MONTO CONVERTIDO",
        valor: money(totalConvertido),
        pie: `${convertidas.length} Folios Convertidos`,
        color: verde,
        fondo: verdeClaro,
      },
      {
        titulo: "TASA DE CIERRE",
        valor: `${pct(totalConvertido, totalCotizado).toFixed(2)}%`,
        pie: "Por Monto Acumulado",
        color: azul,
        fondo: azulClaro,
      },
      {
        titulo: "PENDIENTE",
        valor: money(pendiente),
        pie: `${filtradas.length - convertidas.length} Folios Sin Convertir`,
        color: naranja,
        fondo: naranjaClaro,
      },
    ];

    const gap = 4;
    const tarjW = (usableW - gap * (tarjetas.length - 1)) / tarjetas.length;
    const tarjH = 20;
    tarjetas.forEach((t, i) => {
      const x = ML + i * (tarjW + gap);
      doc.setFillColor(...t.fondo).setDrawColor(...bordeGris).setLineWidth(0.2);
      doc.rect(x, y, tarjW, tarjH, "FD");
      doc.setFillColor(...t.color);
      doc.rect(x, y, tarjW, 1.2, "F");
      doc.setFont(undefined, "bold").setFontSize(7.5).setTextColor(...gris);
      doc.text(t.titulo, x + tarjW / 2, y + 6, { align: "center" });
      doc.setFontSize(13).setTextColor(...t.color);
      doc.text(t.valor, x + tarjW / 2, y + 13, { align: "center" });
      doc.setFont(undefined, "normal").setFontSize(7).setTextColor(...gris);
      doc.text(t.pie, x + tarjW / 2, y + 17.5, { align: "center" });
      doc.setTextColor(0);
    });

    y += tarjH + 7;

    // ---- Detalle folio por folio
    doc.setFillColor(...azul);
    doc.rect(ML, y - 3.6, 1.6, 5, "F");
    doc.setFont(undefined, "bold").setFontSize(10).setTextColor(...azulOscuro);
    doc.text(
      `DETALLE INDIVIDUAL POR FOLIO (${filtradas.length} COTIZACIONES)`,
      ML + 4,
      y
    );
    doc.setTextColor(0).setFont(undefined, "normal");
    y += 2;

    const ordenadas = [...filtradas].sort((a, b) => Number(b.folio) - Number(a.folio));

    doc.autoTable({
      startY: y,
      margin: { left: ML, right: ML, top: 12, bottom: 12 },
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.6,
        lineColor: bordeGris,
        lineWidth: 0.15,
        textColor: 40,
      },
      headStyles: {
        fillColor: azul,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      head: [[
        "#", "EMPRESA", "FOLIO", "FECHA", "CLIENTE / EMPRESA TARGET",
        "SUBTOTAL ($)", "¿CONVERTIDO?", "FEC. CONFIRM.",
      ]],
      columnStyles: {
        0: { cellWidth: 12, halign: "center" },
        1: { cellWidth: 38, halign: "center" },
        2: { cellWidth: 18, halign: "center", fontStyle: "bold" },
        3: { cellWidth: 24, halign: "center" },
        5: { cellWidth: 30, halign: "right", fontStyle: "bold" },
        6: { cellWidth: 26, halign: "center" },
        7: { cellWidth: 28, halign: "center" },
      },
      body: ordenadas.map((c, i) => [
        String(i + 1),
        c.empresa,
        String(c.folio),
        fechaCorta(c.fecha),
        c.cliente,
        money(c.subtotal),
        c.convertida ? "SI" : "NO",
        c.convertida ? fechaCorta(c.fechaPedido) : "-",
      ]),
      foot: [[
        {
          content: `SUMA TOTAL ACUMULADA (${filtradas.length} FOLIOS - ${vendedorNombre.toUpperCase()})`,
          colSpan: 5,
          styles: { halign: "center" },
        },
        money(totalCotizado),
        {
          content: `${money(totalConvertido)} CONVERTIDOS (${pct(
            totalConvertido,
            totalCotizado
          ).toFixed(2)}%)`,
          colSpan: 2,
          styles: { halign: "center" },
        },
      ]],
      footStyles: {
        fillColor: azulClaro,
        textColor: azulOscuro,
        fontStyle: "bold",
        halign: "right",
      },
      // Semáforo del SI/NO como en el ejemplo
      didParseCell: (h) => {
        if (h.section === "body" && h.column.index === 6) {
          const esSi = String(h.cell.raw) === "SI";
          h.cell.styles.fillColor = esSi ? [216, 240, 224] : [253, 226, 226];
          h.cell.styles.textColor = esSi ? [22, 101, 52] : [153, 27, 27];
          h.cell.styles.fontStyle = "bold";
        }
      },
    });

    // ---- Pie
    const paginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      doc.setPage(p);
      doc.setDrawColor(...bordeGris).setLineWidth(0.2);
      doc.line(ML, pageH - 10, pageW - ML, pageH - 10);
      doc.setFont(undefined, "normal").setFontSize(8).setTextColor(...gris);
      doc.text(
        `LANGIANI ERP - Cotizaciones Individuales por Folio: ${vendedorNombre}`,
        ML,
        pageH - 5.5
      );
      doc.text(`Página ${p} de ${paginas}`, pageW - ML, pageH - 5.5, { align: "right" });
      doc.setTextColor(0);
    }

    window.open(doc.output("bloburl"), "_blank");
  };

  const excel = () => {
    if (!filtradas.length) {
      return Swal.fire("Sin datos", "Selecciona un vendedor con cotizaciones", "warning");
    }
    const option = {};
    option.fileName = `ReporteCotizaciones_${vendedorNombre || "Vendedor"}`;
    option.datas = [
      {
        sheetData: filtradas.map((c, i) => ({
          No: i + 1,
          Empresa: c.empresa,
          Folio: c.folio,
          Fecha: c.fecha,
          Cliente: c.cliente,
          Subtotal: c.subtotal,
          Convertido: c.convertida ? "Si" : "No",
          FechaConfirmacion: c.fechaPedido || "",
        })),
        sheetName: "Cotizaciones",
        sheetFilter: ["No","Empresa","Folio","Fecha","Cliente","Subtotal","Convertido","FechaConfirmacion"],
        sheetHeader: ["#","Empresa","Folio","Fecha","Cliente","Subtotal","¿Convertido?","Fec. Confirm."],
      },
    ];
    new ExportJsonExcel(option).saveExcel();
  };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_ventas ? (
        <div className="card container col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuVentas" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFReporte}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Reporte de Cotizaciones por Vendedor</h3>

          <Row>
            <Col md={3}>
              <Label>Vendedor</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedVendedor}
                onChange={(e) => setSelectedVendedor(e.target.value)}
              >
                <option value="">Selecciona un vendedor</option>
                {colaboradores
                  .sort((a, b) => (a.nombre > b.nombre ? 1 : -1))
                  .map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.nombre} {c.apellido}
                    </option>
                  ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
          </Row>

          <br />

          {!selectedVendedor ? (
            <h5 align="center" className="text-muted">
              Selecciona un vendedor para desplegar la información
            </h5>
          ) : (
            <>
              <Row>
                <Col md>
                  <div className="card p-2 text-center" style={{ borderTop: "3px solid #2E75B6" }}>
                    <small className="text-muted">TOTAL COTIZACIONES</small>
                    <h4 style={{ color: "#2E75B6" }}>{filtradas.length} Folios</h4>
                  </div>
                </Col>
                <Col md>
                  <div className="card p-2 text-center" style={{ borderTop: "3px solid #D66E28" }}>
                    <small className="text-muted">MONTO COTIZADO</small>
                    <h4 style={{ color: "#D66E28" }}>{money(totalCotizado)}</h4>
                  </div>
                </Col>
                <Col md>
                  <div className="card p-2 text-center" style={{ borderTop: "3px solid #277C46" }}>
                    <small className="text-muted">MONTO CONVERTIDO</small>
                    <h4 style={{ color: "#277C46" }}>{money(totalConvertido)}</h4>
                    <small className="text-muted">{convertidas.length} Folios Convertidos</small>
                  </div>
                </Col>
                <Col md>
                  <div className="card p-2 text-center" style={{ borderTop: "3px solid #2E75B6" }}>
                    <small className="text-muted">TASA DE CIERRE</small>
                    <h4 style={{ color: "#2E75B6" }}>
                      {pct(totalConvertido, totalCotizado).toFixed(2)}%
                    </h4>
                  </div>
                </Col>
                <Col md>
                  <div className="card p-2 text-center" style={{ borderTop: "3px solid #D66E28" }}>
                    <small className="text-muted">PENDIENTE</small>
                    <h4 style={{ color: "#D66E28" }}>{money(pendiente)}</h4>
                    <small className="text-muted">
                      {filtradas.length - convertidas.length} Folios Sin Convertir
                    </small>
                  </div>
                </Col>
              </Row>

              <br />
              {cargando ? <h5 align="center">Cargando...</h5> : null}

              <Table size="sm" striped borderless className="table-responsive-xl">
                <thead>
                  <tr>
                    <th className="text-center">#</th>
                    <th className="text-center">Empresa</th>
                    <th className="text-center">Folio</th>
                    <th className="text-center">Fecha</th>
                    <th>Cliente</th>
                    <th className="text-end">Subtotal</th>
                    <th className="text-center">¿Convertido?</th>
                    <th className="text-center">Fec. Confirm.</th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.length ? (
                    filtradas.map((c, i) => (
                      <tr key={c._id}>
                        <td className="text-center">{i + 1}</td>
                        <td className="text-center">{c.empresa}</td>
                        <td className="text-center">{c.folio}</td>
                        <td className="text-center">{fechaCorta(c.fecha)}</td>
                        <td>{c.cliente}</td>
                        <td className="text-end">{money(c.subtotal)}</td>
                        <td className="text-center">
                          <Badge color={c.convertida ? "success" : "danger"}>
                            {c.convertida ? "SI" : "NO"}
                          </Badge>
                        </td>
                        <td className="text-center">
                          {c.convertida ? fechaCorta(c.fechaPedido) : "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center text-muted">
                        Ese vendedor no tiene cotizaciones en el periodo
                      </td>
                    </tr>
                  )}
                  {filtradas.length ? (
                    <tr>
                      <td colSpan="5" className="text-center negrita">
                        SUMA TOTAL ACUMULADA ({filtradas.length} FOLIOS)
                      </td>
                      <td className="text-end negrita">{money(totalCotizado)}</td>
                      <td colSpan="2" className="text-center negrita">
                        {money(totalConvertido)} convertidos (
                        {pct(totalConvertido, totalCotizado).toFixed(2)}%)
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </>
          )}
          <br />
        </div>
      ) : undefined}
      <br />
    </>
  );
}

export default ReporteCotizacionesVendedor;
